// src/utils/mathToSpeech.js

/**
 * Converts LaTeX mathematical expressions to spoken equivalents for TTS
 * Handles both simple and complex mathematical structures
 * 
 * @param {string} mathContent - The LaTeX math content to convert
 * @returns {string} - The spoken equivalent of the math expression
 */
export const convertMathToSpeech = (mathContent) => {
  if (!mathContent || typeof mathContent !== 'string') return '';
  
  let result = mathContent;
  
  // Handle complex nested structures first
  
  // Fractions with nested expressions
  result = result.replace(/\\frac\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g, (match, num, den) => {
    const numerator = convertMathToSpeech(num);
    const denominator = convertMathToSpeech(den);
    return `${numerator} over ${denominator}`;
  });
  
  // Square roots with nested content
  result = result.replace(/\\sqrt\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g, (match, content) => {
    const innerContent = convertMathToSpeech(content);
    return `square root of ${innerContent}`;
  });
  
  // nth roots
  result = result.replace(/\\sqrt\[([^\]]+)\]\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}/g, (match, root, content) => {
    const rootValue = convertMathToSpeech(root);
    const innerContent = convertMathToSpeech(content);
    return `${rootValue}th root of ${innerContent}`;
  });
  
  // Integrals with limits
  result = result.replace(/\\int_\{([^}]+)\}\^\{([^}]+)\}/g, (match, lower, upper) => {
    const lowerLimit = convertMathToSpeech(lower);
    const upperLimit = convertMathToSpeech(upper);
    return `integral from ${lowerLimit} to ${upperLimit} of`;
  });
  
  // Definite integrals without limits shown
  result = result.replace(/\\int/g, 'integral of');
  
  // Summations with limits
  result = result.replace(/\\sum_\{([^}]+)\}\^\{([^}]+)\}/g, (match, lower, upper) => {
    const lowerLimit = convertMathToSpeech(lower);
    const upperLimit = convertMathToSpeech(upper);
    return `sum from ${lowerLimit} to ${upperLimit} of`;
  });
  
  // Limits
  result = result.replace(/\\lim_\{([^}]+)\\to([^}]+)\}/g, (match, variable, value) => {
    const varName = convertMathToSpeech(variable);
    const limitValue = convertMathToSpeech(value);
    return `limit as ${varName} approaches ${limitValue} of`;
  });
  
  // Matrix notation
  result = result.replace(/\\begin\{pmatrix\}(.*?)\\end\{pmatrix\}/gs, (match, content) => {
    // Simple matrix conversion - could be enhanced
    const cleanContent = content.replace(/\\\\/g, ' next row ').replace(/&/g, ' ');
    return `matrix with entries ${cleanContent}`;
  });
  
  // Vectors
  result = result.replace(/\\vec\{([^}]+)\}/g, (match, content) => {
    const vectorContent = convertMathToSpeech(content);
    return `vector ${vectorContent}`;
  });
  
  // Absolute value
  result = result.replace(/\\left\|([^|]+)\\right\|/g, (match, content) => {
    const innerContent = convertMathToSpeech(content);
    return `absolute value of ${innerContent}`;
  });
  result = result.replace(/\|([^|]+)\|/g, (match, content) => {
    const innerContent = convertMathToSpeech(content);
    return `absolute value of ${innerContent}`;
  });
  
  // Parentheses
  result = result.replace(/\\left\(/g, 'open parenthesis ');
  result = result.replace(/\\right\)/g, ' close parenthesis');
  result = result.replace(/\\left\[/g, 'open bracket ');
  result = result.replace(/\\right\]/g, ' close bracket');
  
  // Logarithms with bases
  result = result.replace(/\\log_\{([^}]+)\}/g, (match, base) => {
    const baseValue = convertMathToSpeech(base);
    return `log base ${baseValue}`;
  });
  
  // Now handle basic expressions
  
  // Basic fractions (for any remaining simple fractions)
  result = result.replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '$1 over $2');
  
  // Exponents and subscripts
  result = result.replace(/\^(\{[^}]+\}|\w+)/g, (match, exp) => {
    const exponent = exp.replace(/[{}]/g, '');
    return ` to the power of ${exponent}`;
  });
  result = result.replace(/_(\{[^}]+\}|\w+)/g, (match, sub) => {
    const subscript = sub.replace(/[{}]/g, '');
    return ` subscript ${subscript}`;
  });
  
  // Greek letters
  result = result.replace(/\\pi/g, 'pi');
  result = result.replace(/\\theta/g, 'theta');
  result = result.replace(/\\alpha/g, 'alpha');
  result = result.replace(/\\beta/g, 'beta');
  result = result.replace(/\\gamma/g, 'gamma');
  result = result.replace(/\\delta/g, 'delta');
  result = result.replace(/\\epsilon/g, 'epsilon');
  result = result.replace(/\\lambda/g, 'lambda');
  result = result.replace(/\\mu/g, 'mu');
  result = result.replace(/\\sigma/g, 'sigma');
  result = result.replace(/\\phi/g, 'phi');
  result = result.replace(/\\omega/g, 'omega');
  
  // Trigonometric functions
  result = result.replace(/\\sin/g, 'sine');
  result = result.replace(/\\cos/g, 'cosine');
  result = result.replace(/\\tan/g, 'tangent');
  result = result.replace(/\\sec/g, 'secant');
  result = result.replace(/\\csc/g, 'cosecant');
  result = result.replace(/\\cot/g, 'cotangent');
  result = result.replace(/\\arcsin/g, 'arcsine');
  result = result.replace(/\\arccos/g, 'arccosine');
  result = result.replace(/\\arctan/g, 'arctangent');
  
  // Logarithmic functions
  result = result.replace(/\\log/g, 'log');
  result = result.replace(/\\ln/g, 'natural log');
  
  // Other mathematical symbols
  result = result.replace(/\\sum/g, 'sum');
  result = result.replace(/\\prod/g, 'product');
  result = result.replace(/\\lim/g, 'limit');
  result = result.replace(/\\infty/g, 'infinity');
  result = result.replace(/\\partial/g, 'partial');
  result = result.replace(/\\nabla/g, 'nabla');
  
  // Comparison operators
  result = result.replace(/\\leq/g, 'less than or equal to');
  result = result.replace(/\\geq/g, 'greater than or equal to');
  result = result.replace(/\\neq/g, 'not equal to');
  result = result.replace(/\\approx/g, 'approximately equal to');
  result = result.replace(/\\equiv/g, 'equivalent to');
  result = result.replace(/\\propto/g, 'proportional to');
  
  // Arithmetic operators
  result = result.replace(/\\pm/g, 'plus or minus');
  result = result.replace(/\\mp/g, 'minus or plus');
  result = result.replace(/\\times/g, 'times');
  result = result.replace(/\\div/g, 'divided by');
  result = result.replace(/\\cdot/g, 'dot');
  result = result.replace(/\\bullet/g, 'bullet');
  
  // Set notation
  result = result.replace(/\\in/g, 'in');
  result = result.replace(/\\notin/g, 'not in');
  result = result.replace(/\\subset/g, 'subset of');
  result = result.replace(/\\supset/g, 'superset of');
  result = result.replace(/\\cap/g, 'intersection');
  result = result.replace(/\\cup/g, 'union');
  result = result.replace(/\\emptyset/g, 'empty set');
  
  // Handle simple arithmetic expressions
  result = result.replace(/(\d+)\/(\d+)/g, '$1 over $2');
  result = result.replace(/([a-zA-Z]+)\^(\d+)/g, '$1 to the power of $2');
  result = result.replace(/([a-zA-Z]+)_(\d+)/g, '$1 subscript $2');
  
  // Clean up formatting
  result = result.replace(/[{}]/g, ''); // Remove remaining braces
  result = result.replace(/\\/g, ''); // Remove remaining backslashes
  result = result.replace(/\s+/g, ' '); // Normalize whitespace
  result = result.trim();
  
  return result;
};

/**
 * Sanitizes text for TTS by converting math expressions to speech 
 * and removing/cleaning markdown formatting
 * 
 * @param {string} text - The text content to sanitize
 * @returns {string} - The sanitized text ready for TTS
 */
export const sanitizeTextForTTS = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  return text
    // Convert block math expressions
    .replace(/\$\$([^$]+)\$\$/g, (match, mathContent) => {
      const spokenMath = convertMathToSpeech(mathContent);
      return ` ${spokenMath} `;
    })
    // Convert inline math expressions
    .replace(/\$([^$]+)\$/g, (match, mathContent) => {
      const spokenMath = convertMathToSpeech(mathContent);
      return ` ${spokenMath} `;
    })
    // Remove code blocks but indicate their presence
    .replace(/```[\s\S]*?```/g, ' [code block] ')
    // Keep inline code content but remove backticks
    .replace(/`([^`]*?)`/g, '$1')
    // Remove markdown formatting but keep content
    .replace(/\*\*(.*?)\*\*/g, '$1') // Bold
    .replace(/\*(.*?)\*/g, '$1') // Italic
    .replace(/__(.*?)__/g, '$1') // Bold
    .replace(/_(.*?)_/g, '$1') // Italic
    // Remove image references
    .replace(/!\[.*?\]\(.*?\)/g, '')
    // Convert links to just the text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Clean up whitespace
    .replace(/\s+/g, ' ')
    .trim();
};
